import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useLocation, useLocation as useWouterLocation } from "wouter";
import { Calendar, FileText, Plus, Trash2, UserMinus, UserPlus, Users, X } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import {
  useGroup,
  useGroupsCrud,
  useCandidatesList,
  useUsersByRole,
  useSessionsForGroup,
  useSessionsCrud,
  useGroupsList,
} from "@/store/hooks";
import { useDemoStore } from "@/store/useDemoStore";
import { useCandidatesCrud } from "@/store/hooks";
import type { Candidate, Group, GroupDocument, GroupSession, GroupSessionTipo } from "@/data";

const TIPO_LABEL: Record<GroupSessionTipo, string> = {
  induccion: "Inducción",
  teoria: "Teoría",
  practica: "Práctica",
  examen: "Examen",
  cierre: "Cierre",
};

type TabKey = "candidatos" | "sesiones" | "evaluadores" | "documentos";

export default function GroupDetail() {
  const params = useParams<{ groupId: string }>();
  const [location] = useLocation();
  const [, navigate] = useWouterLocation();
  const search = typeof window !== "undefined" ? window.location.search : "";
  const qs = new URLSearchParams(search);
  const tabFromUrl = (qs.get("tab") as TabKey) || "candidatos";
  const sessionFromUrl = qs.get("s");
  const dateFromUrl = qs.get("date");
  const [tab, setTab] = useState<TabKey>(tabFromUrl);
  void location;

  const group = useGroup(params.groupId);
  const groups = useGroupsList();
  const groupsCrud = useGroupsCrud();
  const candidatesCrud = useCandidatesCrud();
  const candidates = useCandidatesList();
  const instructors = useUsersByRole("instructor");
  const evaluators = useUsersByRole("evaluador");
  const sessions = useSessionsForGroup(params.groupId);
  const sessionsCrud = useSessionsCrud(params.groupId ?? "");
  const logEvent = useDemoStore((s) => s.logEvent);
  const enrollInStandard = useDemoStore((s) => s.enrollCandidateInStandard);
  const unenrollFromStandard = useDemoStore((s) => s.unenrollCandidateFromStandard);
  const allGroups = groups;

  if (!group) {
    return (
      <AppLayout breadcrumb={["Grupos", "—"]}>
        <Card>
          <div style={{ padding: 20, textAlign: "center" }}>
            <div style={{ fontSize: 14, color: "var(--cc-gray-600)" }}>Generación no encontrada.</div>
            <Link href="/admin/grupos"><PrimaryButton variant="outline">← Volver</PrimaryButton></Link>
          </div>
        </Card>
      </AppLayout>
    );
  }

  const enrolled = candidates.filter((c) => group.candidatosIds.includes(c.id));
  const pool = candidates.filter((c) => !group.candidatosIds.includes(c.id));
  const cuposLibres = Math.max(0, group.cupo - group.candidatosIds.length);

  const instructor = instructors.find((i) => i.id === group.instructorId);
  const evalIds = group.evaluadoresIds ?? [group.evaluadorId];
  const evaluadoresAsignados = evaluators.filter((e) => evalIds.includes(e.id));

  function enrollCand(id: string) {
    if (cuposLibres <= 0) return;
    groupsCrud.update(group!.id, { candidatosIds: [...group!.candidatosIds, id] });
    enrollInStandard(id, group!.estandar, group!.id);
    logEvent({ actor: "Administrador", rol: "admin", accion: "inscribió candidato", entidad: "group", entidadId: group!.id });
  }
  function unenroll(id: string) {
    groupsCrud.update(group!.id, { candidatosIds: group!.candidatosIds.filter((x) => x !== id) });
    // Only revoke the candidate-↔standard enrollment if no other group for that standard still has them
    const otherStandardGroups = Object.values(allGroups).some(
      (g) => g.id !== group!.id && g.estandar === group!.estandar && g.candidatosIds.includes(id)
    );
    if (!otherStandardGroups) unenrollFromStandard(id, group!.estandar);
    logEvent({ actor: "Administrador", rol: "admin", accion: "quitó candidato", entidad: "group", entidadId: group!.id });
  }

  return (
    <AppLayout breadcrumb={["Grupos", group.nombre]}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>{group.nombre}</h1>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>
            {group.estandar} · {group.inicio} → {group.fin} · {group.sede}
          </div>
          <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center" }}>
            <Badge tone={group.estado === "en-curso" ? "green" : group.estado === "planeado" ? "amber" : "gray"}>
              {group.estado === "en-curso" ? "En curso" : group.estado === "planeado" ? "Planeado" : "Finalizado"}
            </Badge>
            <span style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>
              {enrolled.length}/{group.cupo} inscritos · {cuposLibres} cupos libres
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => {
              if (!window.confirm(`Eliminar generación "${group.nombre}"? Esta acción no se puede deshacer.`)) return;
              groupsCrud.remove(group.id);
              logEvent({ actor: "Administrador", rol: "admin", accion: "eliminó generación", entidad: "group", entidadId: group.id });
              navigate("/admin/grupos");
            }}
            data-testid="button-delete-group"
            style={{ border: "1px solid var(--cc-red-bg)", background: "white", color: "var(--cc-red)", padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Trash2 size={13} /> Eliminar
          </button>
          <Link href="/admin/grupos"><PrimaryButton variant="outline">← Volver</PrimaryButton></Link>
        </div>
      </div>

      <Card padding={0}>
        <div style={{ display: "flex", borderBottom: "1px solid var(--cc-gray-200)" }}>
          {([
            { k: "candidatos", icon: Users, label: `Candidatos (${enrolled.length})` },
            { k: "sesiones", icon: Calendar, label: `Sesiones (${sessions.length})` },
            { k: "evaluadores", icon: Users, label: `Evaluadores (${evaluadoresAsignados.length})` },
            { k: "documentos", icon: FileText, label: `Documentos (${(group.documentos ?? []).length})` },
          ] as const).map((t) => {
            const Icon = t.icon;
            const active = tab === t.k;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`tab-${t.k}`}
                style={{
                  flex: 1, padding: "12px 14px", border: "none", cursor: "pointer",
                  background: active ? "var(--cc-teal-bg)" : "white",
                  color: active ? "var(--cc-teal)" : "var(--cc-gray-700)",
                  fontWeight: 600, fontSize: 13,
                  borderBottom: active ? "2px solid var(--cc-teal)" : "2px solid transparent",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
        <div style={{ padding: 18 }}>
          {tab === "candidatos" && (
            <CandidatosTab
              enrolled={enrolled}
              pool={pool}
              cuposLibres={cuposLibres}
              onEnroll={enrollCand}
              onUnenroll={unenroll}
              onCreate={(c) => {
                candidatesCrud.add(c);
                if (cuposLibres > 0) {
                  groupsCrud.update(group.id, { candidatosIds: [...group.candidatosIds, c.id] });
                  enrollInStandard(c.id, group.estandar, group.id);
                }
                logEvent({ actor: "Administrador", rol: "admin", accion: "creó candidato", entidad: "candidate", entidadId: c.id });
              }}
            />
          )}
          {tab === "sesiones" && (
            <SesionesTab
              groupId={group.id}
              sede={group.sede}
              sessions={sessions}
              instructors={instructors.map((i) => ({ id: i.id, nombre: i.nombre }))}
              evaluatorsAvailable={evaluadoresAsignados.map((e) => ({ id: e.id, nombre: e.nombre }))}
              openSessionId={sessionFromUrl}
              prefillDate={dateFromUrl}
              onUpsert={(s) => { sessionsCrud.upsert(s); logEvent({ actor: "Administrador", rol: "admin", accion: "actualizó sesión", entidad: "session", entidadId: s.id }); }}
              onRemove={(id) => { sessionsCrud.remove(id); logEvent({ actor: "Administrador", rol: "admin", accion: "eliminó sesión", entidad: "session", entidadId: id }); }}
            />
          )}
          {tab === "evaluadores" && (
            <EvaluadoresTab
              group={group}
              allGroups={groups}
              evaluators={evaluators.map((e) => ({ id: e.id, nombre: e.nombre, iniciales: e.iniciales, capacidad: e.capacidad ?? 12 }))}
              onChange={(ids) => {
                groupsCrud.update(group.id, { evaluadoresIds: ids, evaluadorId: ids[0] ?? group.evaluadorId });
                logEvent({ actor: "Administrador", rol: "admin", accion: "actualizó evaluadores", entidad: "group", entidadId: group.id });
              }}
            />
          )}
          {tab === "documentos" && (
            <DocumentosTab
              documentos={group.documentos ?? []}
              onChange={(docs) => {
                groupsCrud.update(group.id, { documentos: docs });
                logEvent({ actor: "Administrador", rol: "admin", accion: "actualizó documentos", entidad: "group", entidadId: group.id });
              }}
            />
          )}
        </div>
      </Card>

      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Card>
          <SectionTitle>Instructor</SectionTitle>
          <Row label="Nombre" value={instructor?.nombre ?? "—"} sub={instructor?.notas} />
          <Row label="Sede" value={group.sede} />
        </Card>
        <Card>
          <SectionTitle>Resumen del grupo</SectionTitle>
          <Row label="Estado" value={group.estado} />
          <Row label="Cupo" value={`${enrolled.length} / ${group.cupo}`} sub={`${cuposLibres} cupos libres`} />
          <Row label="Evaluadores" value={`${evaluadoresAsignados.length} asignados`} />
        </Card>
      </div>
    </AppLayout>
  );
}

function CandidatosTab({ enrolled, pool, cuposLibres, onEnroll, onUnenroll, onCreate }: {
  enrolled: ReturnType<typeof useCandidatesList>;
  pool: ReturnType<typeof useCandidatesList>;
  cuposLibres: number;
  onEnroll: (id: string) => void;
  onUnenroll: (id: string) => void;
  onCreate: (c: Candidate) => void;
}) {
  const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const filteredPool = pool.filter((c) => c.nombre.toLowerCase().includes(q.toLowerCase()) || c.matricula.toLowerCase().includes(q.toLowerCase()));
  return (
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
      <div>
        <SectionTitle>Inscritos</SectionTitle>
        {enrolled.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--cc-gray-500)", padding: 14, textAlign: "center", border: "1px dashed var(--cc-gray-200)", borderRadius: 8 }}>
            Aún no hay candidatos inscritos.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--cc-gray-50)", textAlign: "left" }}>
                {["Candidato", "Unidad", "Estado", ""].map((h) => (
                  <th key={h} style={{ padding: "8px 10px", fontSize: 10, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {enrolled.map((c) => (
                <tr key={c.id} style={{ borderTop: "1px solid var(--cc-gray-100)" }} data-testid={`row-cand-${c.id}`}>
                  <td style={{ padding: "8px 10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: c.avatarColor, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>{c.iniciales}</div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{c.nombre}</div>
                        <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{c.matricula}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "8px 10px", color: "var(--cc-gray-700)", fontSize: 12 }}>{c.unidad}</td>
                  <td style={{ padding: "8px 10px" }}><Badge tone={statusTone(c.estado)}>{c.estado.replace("-", " ")}</Badge></td>
                  <td style={{ padding: "8px 10px" }}>
                    <button onClick={() => onUnenroll(c.id)} data-testid={`button-unenroll-${c.id}`}
                      style={{ border: "1px solid var(--cc-red-bg)", background: "white", color: "var(--cc-red)", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <UserMinus size={12} /> Quitar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <SectionTitle>Inscribir candidato</SectionTitle>
          <button onClick={() => setNewOpen(true)} data-testid="button-new-candidate"
            style={{ border: "1px solid var(--cc-teal)", background: "var(--cc-teal-bg)", color: "var(--cc-teal)", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Plus size={12} /> Nuevo
          </button>
        </div>
        <div style={{ fontSize: 11, color: cuposLibres === 0 ? "var(--cc-red)" : "var(--cc-gray-500)", marginBottom: 8 }}>
          {cuposLibres === 0 ? "Cupo lleno — libere un lugar para inscribir más." : `${cuposLibres} cupos disponibles`}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar candidato del catálogo…" data-testid="search-pool"
          style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--cc-gray-200)", borderRadius: 6, fontSize: 13, marginBottom: 8 }} />
        <div style={{ maxHeight: 320, overflow: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
          {filteredPool.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--cc-gray-500)", padding: 10, textAlign: "center" }}>Sin coincidencias.</div>
          )}
          {filteredPool.map((c) => (
            <button key={c.id} onClick={() => onEnroll(c.id)} disabled={cuposLibres === 0} data-testid={`button-enroll-${c.id}`}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, border: "1px solid var(--cc-gray-100)", borderRadius: 8, background: "white", cursor: cuposLibres === 0 ? "not-allowed" : "pointer", opacity: cuposLibres === 0 ? 0.5 : 1, textAlign: "left" }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", background: c.avatarColor, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>{c.iniciales}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)" }}>{c.nombre}</div>
                <div style={{ fontSize: 11, color: "var(--cc-gray-500)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.unidad}</div>
              </div>
              <UserPlus size={14} color="var(--cc-teal)" />
            </button>
          ))}
        </div>
      </div>
      {newOpen && (
        <NewCandidateModal
          onClose={() => setNewOpen(false)}
          onSave={(c) => { onCreate(c); setNewOpen(false); }}
          canEnroll={cuposLibres > 0}
        />
      )}
    </div>
  );
}

const AVATAR_COLORS = ["#0e7490", "#1a2744", "#16a34a", "#d97706", "#dc2626", "#7c3aed", "#0891b2", "#475569"];

function NewCandidateModal({ onClose, onSave, canEnroll }: { onClose: () => void; onSave: (c: Candidate) => void; canEnroll: boolean }) {
  const [nombre, setNombre] = useState("");
  const [rango, setRango] = useState("Cabo");
  const [unidad, setUnidad] = useState("");
  const [matricula, setMatricula] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [err, setErr] = useState<string | null>(null);

  function save() {
    if (!nombre.trim() || !matricula.trim() || !unidad.trim()) {
      setErr("Nombre, unidad y matrícula son obligatorios.");
      return;
    }
    const parts = nombre.trim().split(/\s+/);
    const iniciales = ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase() || "??";
    const c: Candidate = {
      id: `cand-${Date.now().toString(36)}`,
      nombre: nombre.trim(),
      rango,
      unidad: unidad.trim(),
      curp: "",
      matricula: matricula.trim(),
      edad: 30,
      estatura: "—",
      experiencia: "—",
      email: email.trim(),
      telefono: telefono.trim(),
      estado: "inscrito",
      progreso: 0,
      ultimaActividad: new Date().toISOString().slice(0, 10),
      avatarColor: AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)],
      iniciales,
    };
    onSave(c);
  }

  return (
    <div onClick={onClose} style={modalOverlay}>
      <div onClick={(e) => e.stopPropagation()} style={modalBody}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: "var(--cc-navy)" }}>Nuevo candidato</h2>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer" }}><X size={18} /></button>
        </div>
        {err && <div style={{ background: "var(--cc-red-bg)", color: "var(--cc-red)", padding: "6px 10px", borderRadius: 6, fontSize: 12, marginBottom: 8 }}>{err}</div>}
        <Field label="Nombre completo"><input value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid="cand-nombre" style={inputStyle} /></Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Rango">
            <select value={rango} onChange={(e) => setRango(e.target.value)} style={inputStyle}>
              {["Cabo", "Sargento 2°", "Sargento 1°", "Suboficial", "Oficial"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Matrícula"><input value={matricula} onChange={(e) => setMatricula(e.target.value)} data-testid="cand-matricula" style={inputStyle} /></Field>
        </div>
        <Field label="Unidad / corporación"><input value={unidad} onChange={(e) => setUnidad(e.target.value)} data-testid="cand-unidad" style={inputStyle} /></Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Email"><input value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} /></Field>
          <Field label="Teléfono"><input value={telefono} onChange={(e) => setTelefono(e.target.value)} style={inputStyle} /></Field>
        </div>
        <div style={{ fontSize: 11, color: "var(--cc-gray-500)", marginTop: 8 }}>
          {canEnroll ? "Se inscribirá automáticamente en esta generación." : "Cupo lleno — se creará en el catálogo sin inscribir."}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
          <PrimaryButton variant="outline" onClick={onClose}>Cancelar</PrimaryButton>
          <PrimaryButton onClick={save} testId="cand-save">Guardar</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function SesionesTab({ groupId, sede, sessions, instructors, evaluatorsAvailable, openSessionId, prefillDate, onUpsert, onRemove }: {
  groupId: string;
  sede: string;
  sessions: GroupSession[];
  instructors: Array<{ id: string; nombre: string }>;
  evaluatorsAvailable: Array<{ id: string; nombre: string }>;
  openSessionId?: string | null;
  prefillDate?: string | null;
  onUpsert: (s: GroupSession) => void;
  onRemove: (id: string) => void;
}) {
  const [editing, setEditing] = useState<GroupSession | null>(null);
  const ordered = useMemo(() => [...sessions].sort((a: GroupSession, b: GroupSession) => (a.fecha + a.hora).localeCompare(b.fecha + b.hora)), [sessions]);

  function newSession(date?: string) {
    const today = date ?? new Date().toISOString().slice(0, 10);
    setEditing({
      id: `ses-${Date.now().toString(36)}`,
      grupoId: groupId,
      titulo: "Nueva sesión",
      tipo: "teoria",
      fecha: today,
      hora: "09:00",
      duracionMin: 120,
      sede,
      instructorId: instructors[0]?.id,
    });
  }

  useEffect(() => {
    if (openSessionId) {
      const target = sessions.find((s) => s.id === openSessionId);
      if (target) setEditing(target);
    } else if (prefillDate) {
      newSession(prefillDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSessionId, prefillDate]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <SectionTitle>Sesiones programadas</SectionTitle>
        <PrimaryButton size="sm" onClick={() => newSession()} testId="button-new-session"><Plus size={12} /> Nueva sesión</PrimaryButton>
      </div>
      {ordered.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--cc-gray-500)", padding: 14, textAlign: "center", border: "1px dashed var(--cc-gray-200)", borderRadius: 8 }}>
          Sin sesiones programadas. Cree la primera.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "var(--cc-gray-50)", textAlign: "left" }}>
              {["Fecha", "Hora", "Tipo", "Título", "Sede", "Evaluador", ""].map((h) => (
                <th key={h} style={{ padding: "8px 10px", fontSize: 10, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ordered.map((s) => (
              <tr key={s.id} style={{ borderTop: "1px solid var(--cc-gray-100)", cursor: "pointer" }} onClick={() => setEditing(s)} data-testid={`row-session-${s.id}`}>
                <td style={{ padding: "8px 10px", fontSize: 12, fontFamily: "var(--app-font-mono)" }}>{s.fecha}</td>
                <td style={{ padding: "8px 10px", fontSize: 12 }}>{s.hora}</td>
                <td style={{ padding: "8px 10px" }}><Badge tone="navy">{TIPO_LABEL[s.tipo]}</Badge></td>
                <td style={{ padding: "8px 10px", fontWeight: 600, color: "var(--cc-navy)" }}>{s.titulo}</td>
                <td style={{ padding: "8px 10px", fontSize: 12, color: "var(--cc-gray-700)" }}>{s.sede}</td>
                <td style={{ padding: "8px 10px", fontSize: 12, color: "var(--cc-gray-700)" }}>
                  {s.evaluadorId ? (evaluatorsAvailable.find((e) => e.id === s.evaluadorId)?.nombre ?? s.evaluadorId) : "—"}
                </td>
                <td style={{ padding: "8px 10px" }}>
                  <button onClick={(e) => { e.stopPropagation(); onRemove(s.id); }} data-testid={`button-del-session-${s.id}`}
                    style={{ border: "none", background: "transparent", color: "var(--cc-red)", cursor: "pointer" }}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <SessionEditor
          session={editing}
          instructors={instructors}
          evaluators={evaluatorsAvailable}
          onClose={() => setEditing(null)}
          onSave={(s) => { onUpsert(s); setEditing(null); }}
        />
      )}
    </div>
  );
}

function SessionEditor({ session, instructors, evaluators, onClose, onSave }: {
  session: GroupSession;
  instructors: Array<{ id: string; nombre: string }>;
  evaluators: Array<{ id: string; nombre: string }>;
  onClose: () => void;
  onSave: (s: GroupSession) => void;
}) {
  const [s, setS] = useState<GroupSession>(session);
  return (
    <div onClick={onClose} style={modalOverlay}>
      <div onClick={(e) => e.stopPropagation()} style={modalBody}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: "var(--cc-navy)" }}>Sesión</h2>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer" }}><X size={18} /></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Título"><input value={s.titulo} onChange={(e) => setS({ ...s, titulo: e.target.value })} data-testid="ses-titulo" style={inputStyle} /></Field>
          <Field label="Tipo">
            <select value={s.tipo} onChange={(e) => setS({ ...s, tipo: e.target.value as GroupSessionTipo })} data-testid="ses-tipo" style={inputStyle}>
              {(Object.keys(TIPO_LABEL) as GroupSessionTipo[]).map((t) => <option key={t} value={t}>{TIPO_LABEL[t]}</option>)}
            </select>
          </Field>
          <Field label="Fecha"><input type="date" value={s.fecha} onChange={(e) => setS({ ...s, fecha: e.target.value })} data-testid="ses-fecha" style={inputStyle} /></Field>
          <Field label="Hora"><input type="time" value={s.hora} onChange={(e) => setS({ ...s, hora: e.target.value })} data-testid="ses-hora" style={inputStyle} /></Field>
          <Field label="Duración (min)"><input type="number" min={15} value={s.duracionMin} onChange={(e) => setS({ ...s, duracionMin: Number(e.target.value) || 0 })} style={inputStyle} /></Field>
          <Field label="Sede"><input value={s.sede} onChange={(e) => setS({ ...s, sede: e.target.value })} style={inputStyle} /></Field>
          <Field label="Instructor">
            <select value={s.instructorId ?? ""} onChange={(e) => setS({ ...s, instructorId: e.target.value || undefined })} style={inputStyle}>
              <option value="">—</option>
              {instructors.map((i) => <option key={i.id} value={i.id}>{i.nombre}</option>)}
            </select>
          </Field>
          <Field label="Evaluador">
            <select value={s.evaluadorId ?? ""} onChange={(e) => setS({ ...s, evaluadorId: e.target.value || undefined })} style={inputStyle}>
              <option value="">—</option>
              {evaluators.map((ev) => <option key={ev.id} value={ev.id}>{ev.nombre}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Notas"><textarea value={s.notas ?? ""} onChange={(e) => setS({ ...s, notas: e.target.value })} rows={2} style={{ ...inputStyle, resize: "vertical" }} /></Field>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
          <PrimaryButton variant="outline" onClick={onClose}>Cancelar</PrimaryButton>
          <PrimaryButton onClick={() => onSave(s)} testId="ses-save">Guardar</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function EvaluadoresTab({ group, allGroups, evaluators, onChange }: {
  group: Group;
  allGroups: Group[];
  evaluators: Array<{ id: string; nombre: string; iniciales: string; capacidad: number }>;
  onChange: (ids: string[]) => void;
}) {
  const current = group.evaluadoresIds ?? [group.evaluadorId];

  function load(evalId: string) {
    return allGroups.reduce((acc, g) => {
      const ids = g.evaluadoresIds ?? [g.evaluadorId];
      return acc + (ids.includes(evalId) ? g.candidatosIds.length : 0);
    }, 0);
  }

  function toggle(id: string) {
    if (current.includes(id)) {
      if (current.length === 1) return;
      onChange(current.filter((x) => x !== id));
    } else {
      const ev = evaluators.find((e) => e.id === id)!;
      const wouldAssign = load(id) - (current.includes(id) ? group.candidatosIds.length : 0) + group.candidatosIds.length;
      if (wouldAssign > ev.capacidad) return;
      onChange([...current, id]);
    }
  }

  return (
    <div>
      <SectionTitle>Evaluadores asignados ({current.length})</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {evaluators.map((e) => {
          const checked = current.includes(e.id);
          const totalLoad = load(e.id);
          const wouldExceed = !checked && totalLoad + group.candidatosIds.length > e.capacidad;
          return (
            <label key={e.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: 12,
              border: `1px solid ${checked ? "var(--cc-teal)" : "var(--cc-gray-200)"}`,
              borderRadius: 8, background: checked ? "var(--cc-teal-bg)" : "white",
              opacity: wouldExceed ? 0.55 : 1, cursor: wouldExceed ? "not-allowed" : "pointer",
            }} data-testid={`eval-toggle-${e.id}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(e.id)} disabled={wouldExceed} />
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--cc-navy)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>{e.iniciales}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{e.nombre}</div>
                <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>
                  Carga: {totalLoad} / {e.capacidad} candidatos {wouldExceed && "· excede capacidad"}
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function DocumentosTab({ documentos, onChange }: {
  documentos: GroupDocument[];
  onChange: (docs: GroupDocument[]) => void;
}) {
  const [editing, setEditing] = useState<GroupDocument | null>(null);
  function newDoc() {
    setEditing({
      id: `doc-${Date.now().toString(36)}`,
      nombre: "",
      tipo: "otro",
      fecha: new Date().toISOString().slice(0, 10),
      subidoPor: "Administrador",
    });
  }
  function save(d: GroupDocument) {
    const idx = documentos.findIndex((x) => x.id === d.id);
    if (idx === -1) onChange([...documentos, d]);
    else onChange(documentos.map((x, i) => (i === idx ? d : x)));
    setEditing(null);
  }
  function remove(id: string) { onChange(documentos.filter((x) => x.id !== id)); }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <SectionTitle>Documentos del grupo</SectionTitle>
        <PrimaryButton size="sm" onClick={newDoc} testId="button-new-doc"><Plus size={12} /> Nuevo documento</PrimaryButton>
      </div>
      {documentos.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--cc-gray-500)", padding: 14, textAlign: "center", border: "1px dashed var(--cc-gray-200)", borderRadius: 8 }}>
          Sin documentos. Registre el primero (los archivos se guardan como metadatos).
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "var(--cc-gray-50)", textAlign: "left" }}>
              {["Documento", "Tipo", "Fecha", "Subido por", ""].map((h) => (
                <th key={h} style={{ padding: "8px 10px", fontSize: 10, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {documentos.map((d) => (
              <tr key={d.id} style={{ borderTop: "1px solid var(--cc-gray-100)", cursor: "pointer" }} onClick={() => setEditing(d)} data-testid={`row-doc-${d.id}`}>
                <td style={{ padding: "8px 10px", fontWeight: 600, color: "var(--cc-navy)" }}>{d.nombre}</td>
                <td style={{ padding: "8px 10px" }}><Badge tone="navy">{d.tipo}</Badge></td>
                <td style={{ padding: "8px 10px", fontFamily: "var(--app-font-mono)", fontSize: 12 }}>{d.fecha}</td>
                <td style={{ padding: "8px 10px", fontSize: 12 }}>{d.subidoPor}</td>
                <td style={{ padding: "8px 10px" }}>
                  <button onClick={(e) => { e.stopPropagation(); remove(d.id); }} data-testid={`button-del-doc-${d.id}`}
                    style={{ border: "none", background: "transparent", color: "var(--cc-red)", cursor: "pointer" }}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <div onClick={() => setEditing(null)} style={modalOverlay}>
          <div onClick={(e) => e.stopPropagation()} style={modalBody}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: "var(--cc-navy)" }}>Documento</h2>
              <button onClick={() => setEditing(null)} style={{ border: "none", background: "transparent", cursor: "pointer" }}><X size={18} /></button>
            </div>
            <Field label="Nombre"><input value={editing.nombre} onChange={(e) => setEditing({ ...editing, nombre: e.target.value })} data-testid="doc-nombre" style={inputStyle} /></Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="Tipo">
                <select value={editing.tipo} onChange={(e) => setEditing({ ...editing, tipo: e.target.value as GroupDocument["tipo"] })} style={inputStyle}>
                  {(["convenio", "lista", "plan", "acta", "otro"] as const).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Fecha"><input type="date" value={editing.fecha} onChange={(e) => setEditing({ ...editing, fecha: e.target.value })} style={inputStyle} /></Field>
            </div>
            <Field label="Subido por"><input value={editing.subidoPor} onChange={(e) => setEditing({ ...editing, subidoPor: e.target.value })} style={inputStyle} /></Field>
            <Field label="Notas"><textarea value={editing.notas ?? ""} onChange={(e) => setEditing({ ...editing, notas: e.target.value })} rows={2} style={{ ...inputStyle, resize: "vertical" }} /></Field>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <PrimaryButton variant="outline" onClick={() => setEditing(null)}>Cancelar</PrimaryButton>
              <PrimaryButton onClick={() => save(editing)} testId="doc-save">Guardar</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function statusTone(e: string): "green" | "amber" | "teal" | "navy" | "gray" | "red" {
  if (e === "certificado") return "green";
  if (e === "en-dictamen") return "amber";
  if (e === "en-evaluacion") return "teal";
  if (e === "inscrito") return "navy";
  if (e === "no-competente") return "red";
  return "gray";
}

function Row({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ padding: "8px 0", borderBottom: "1px solid var(--cc-gray-100)" }}>
      <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{sub}</div>}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", marginTop: 8 }}>
      <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = { width: "100%", padding: "8px 10px", border: "1px solid var(--cc-gray-200)", borderRadius: 6, fontSize: 13, background: "white" };
const modalOverlay: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 };
const modalBody: React.CSSProperties = { background: "white", borderRadius: 12, padding: 22, width: "min(560px, 100%)", maxHeight: "90vh", overflow: "auto" };
